import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Shared";
import ResetPasswordModal from "@/components/ResetPasswordModal";
import { toast } from "sonner";
import { Building2, Plus, X, Upload, FileSpreadsheet, Loader2, Download, KeyRound, Mail, FileText } from "lucide-react";
import DocumentsModal from "@/components/DocumentsModal";

export default function AdminSuppliers() {
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [accounts, setAccounts] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [docsUser, setDocsUser] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    const [s, p, u] = await Promise.all([
      api.get("/admin/suppliers"),
      api.get("/admin/products"),
      api.get("/admin/users", { params: { role: "supplier" } }),
    ]);
    setItems(s.data);
    const c = {};
    p.data.forEach((pr) => { c[pr.supplier_id] = (c[pr.supplier_id] || 0) + 1; });
    setCounts(c);
    const a = {};
    u.data.forEach((usr) => { if (usr.supplier_id) a[usr.supplier_id] = usr; });
    setAccounts(a);
  };
  useEffect(() => { load().catch(() => setItems([])); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/suppliers", form);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post(`/admin/suppliers/${data.id}/import-products`, fd);
        toast.success(`Fournisseur créé · ${res.data.imported} produits importés`);
      } else {
        toast.success("Fournisseur ajouté");
      }
      setOpen(false); setForm({}); setFile(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const importFor = async (sid, f) => {
    if (!f) return;
    setImportingId(sid);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post(`/admin/suppliers/${sid}/import-products`, fd);
      toast.success(`${res.data.imported} produits importés (${res.data.skipped} ignorés)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImportingId(null); }
  };

  const downloadTemplate = () => {
    const csv = "Designation,Code,Categorie,Marque,Forme,Prix Achat,Prix Public,Quantite\nCreme hydratante 50ml,3401599,Soins,MaMarque,CREME,12.500,24.900,100\n";
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "modele_import_produits.csv";
    a.click();
  };

  if (items === null) return <Loading />;
  const input = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm";

  return (
    <div>
      <PageHeader title="Fournisseurs" subtitle="Gérez les laboratoires et distributeurs partenaires.">
        <div className="flex gap-2">
          <button onClick={downloadTemplate} data-testid="download-template" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition-colors duration-200"><Download className="h-4 w-4" /> Modèle</button>
          <button onClick={() => setOpen(true)} data-testid="add-supplier" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200"><Plus className="h-4 w-4" /> Ajouter</button>
        </div>
      </PageHeader>

      {items.length === 0 ? <EmptyState title="Aucun fournisseur" icon={Building2} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col" data-testid={`supplier-${s.id}`}>
              <div className="h-11 w-11 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center font-bold">{s.name.charAt(0)}</div>
              <div className="mt-3 font-bold text-slate-900">{s.name}</div>
              <div className="text-xs text-slate-400">{s.category}{s.city ? ` · ${s.city}` : ""}</div>
              {s.description && <p className="mt-2 text-sm text-slate-500 line-clamp-2 flex-1">{s.description}</p>}
              {accounts[s.id] && (
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="text-xs font-semibold text-slate-600 num truncate flex-1" data-testid={`supplier-email-${s.id}`}>{accounts[s.id].email}</span>
                  <button onClick={() => setDocsUser({ ...accounts[s.id], supplier_name: s.name })} data-testid={`supplier-docs-${s.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-semibold transition-colors duration-200"><FileText className="h-3 w-3" /> {accounts[s.id].documents_count || 0}</button>
                  <button onClick={() => setResetUser({ ...accounts[s.id], supplier_name: s.name })} data-testid={`supplier-reset-${s.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold transition-colors duration-200"><KeyRound className="h-3 w-3" /> Dépanner</button>
                </div>
              )}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 num">{counts[s.id] || 0} produits</span>
                <input ref={(el) => (fileRefs.current[s.id] = el)} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={(e) => { importFor(s.id, e.target.files[0]); e.target.value = ""; }} data-testid={`import-input-${s.id}`} />
                <button onClick={() => fileRefs.current[s.id]?.click()} disabled={importingId === s.id} data-testid={`import-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold transition-colors duration-200 disabled:opacity-60">
                  {importingId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Importer Excel
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => !saving && setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="supplier-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg text-slate-900">Nouveau fournisseur</h3><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-3">
              <div><label className="text-sm font-semibold text-slate-700">Nom</label><input data-testid="supplier-name" onChange={set("name")} className={input} /></div>
              <div><label className="text-sm font-semibold text-slate-700">Catégorie</label><input data-testid="supplier-category" onChange={set("category")} className={input} placeholder="Parapharmacie" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Ville</label><input onChange={set("city")} className={input} /></div>
              <div><label className="text-sm font-semibold text-slate-700">Description</label><textarea onChange={set("description")} className={input + " h-16 py-2"} /></div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Liste des produits (Excel / CSV) — optionnel</label>
                <label className="mt-1 flex items-center gap-3 px-3 py-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-indigo-400 cursor-pointer transition-colors duration-200">
                  <FileSpreadsheet className="h-5 w-5 text-indigo-500" />
                  <span className="text-sm text-slate-500 flex-1 truncate">{file ? file.name : "Choisir un fichier .xlsx, .xls ou .csv"}</span>
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="supplier-file" onChange={(e) => setFile(e.target.files[0])} />
                </label>
                <button type="button" onClick={downloadTemplate} className="mt-1.5 text-xs font-semibold text-indigo-600 hover:underline">Télécharger le modèle de fichier</button>
              </div>
            </div>
            <button onClick={save} disabled={saving} data-testid="save-supplier" className="mt-5 w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </button>
          </div>
        </div>
      )}
      {docsUser && <DocumentsModal user={docsUser} onClose={() => setDocsUser(null)} />}
      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}
